import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, MapPin, Trophy, Target } from 'lucide-react';
import { adminAPI } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';

const Users = () => {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  const { data, isLoading } = useQuery({
    queryKey: ['users', search, sortBy, sortOrder],
    queryFn: () =>
      adminAPI.getPaginatedUsers({
        page: 1,
        limit: 50,
        search: search || undefined,
        sortBy,
        sortOrder,
      }),
  });

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Users</h1>
        <p className="text-sm sm:text-base text-text-secondary mt-1">
          Manage and view all registered users
        </p>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardContent>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
            <div className="relative flex-1 w-full">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted"
                size={20}
              />
              <input
                type="text"
                placeholder="Search by name, username, or email..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
                className="w-full pl-10 pr-4 py-2 bg-background-elevated border border-border rounded-lg text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-primary text-sm sm:text-base"
              />
            </div>
            <div className="text-xs sm:text-sm text-text-secondary whitespace-nowrap">
              {data?.pagination?.total || 0} total users
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-background-elevated border-b border-border">
                    <tr>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:text-text-primary"
                        onClick={() => handleSort('username')}
                      >
                        Username {getSortIcon('username')}
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:text-primary"
                        onClick={() => handleSort('rating')}
                      >
                        Rating {getSortIcon('rating')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Location
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:text-text-primary"
                        onClick={() => handleSort('gamesPlayed')}
                      >
                        Games {getSortIcon('gamesPlayed')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Win Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Streak
                      </th>
                      <th
                        className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider cursor-pointer hover:text-text-primary"
                        onClick={() => handleSort('createdAt')}
                      >
                        Joined {getSortIcon('createdAt')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase tracking-wider">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {data?.users?.map((user) => (
                      <tr
                        key={user.id}
                        className="hover:bg-background-elevated transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-text-primary">
                              {user.username || 'N/A'}
                            </div>
                            <div className="text-xs text-text-muted">
                              {user.firstName} {user.lastName}
                            </div>
                            <div className="text-xs text-text-muted">
                              {user.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Trophy size={14} className="text-gold" />
                            <span className="text-sm font-semibold text-text-primary">
                              {user.rating}
                            </span>
                          </div>
                          <div className="text-xs text-text-muted mt-1">
                            B:{user.bulletRating} Bl:{user.blitzRating} R:
                            {user.rapidRating}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-start gap-1">
                            <MapPin size={14} className="text-cyan mt-0.5" />
                            <div className="text-sm text-text-secondary">
                              {user.city && `${user.city}, `}
                              {user.state && `${user.state}, `}
                              {user.country || 'Unknown'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-text-primary">
                            {user.gamesPlayed}
                          </div>
                          <div className="text-xs text-text-muted">
                            {user.gamesWon} won
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-text-primary">
                            {user.winRate}%
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Target size={14} className="text-primary" />
                            <div>
                              <div className="text-sm text-text-primary">
                                {user.currentStreak}
                              </div>
                              <div className="text-xs text-text-muted">
                                Max: {user.longestStreak}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-text-secondary">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-col gap-1">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                user.isOnline
                                  ? 'bg-success/10 text-success'
                                  : 'bg-text-muted/10 text-text-muted'
                              }`}
                            >
                              {user.isOnline ? 'Online' : 'Offline'}
                            </span>
                            {user.betaApproved && (
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-primary/10 text-primary">
                                Beta
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Users;
